import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

interface PaginationProps {
  currentPage?: number;
  onClick: (page: number) => void;
  totalCount?: number;
  estimatedTotalCount?: number;
  pageCount?: number;
  itemsPerPage: number;
  showNumberOfPages?: number;
}

const generatePageNumbers = (
  currentPage: number,
  pageCount: number,
  showNumberOfPages: number,
): number[] => {
  const halfShowNumberOfPages = Math.floor(showNumberOfPages / 2);

  let startPage = Math.max(1, currentPage - halfShowNumberOfPages);
  const endPage = Math.min(pageCount, startPage + showNumberOfPages - 1);

  if (endPage - startPage + 1 < showNumberOfPages) {
    startPage = Math.max(1, endPage - showNumberOfPages + 1);
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
};

const generateAnchoredPageNumbers = (
  currentPage: number,
  pageCount: number,
): Array<number | 'ellipsis-start' | 'ellipsis-end'> => {
  if (pageCount <= 5) {
    return generatePageNumbers(currentPage, pageCount, pageCount);
  }

  const middlePages = generatePageNumbers(currentPage, pageCount, 3).filter(
    (page) => page > 1 && page < pageCount,
  );
  const pages: Array<number | 'ellipsis-start' | 'ellipsis-end'> = [1];
  const firstMiddlePage = middlePages[0];
  const lastMiddlePage = middlePages[middlePages.length - 1];

  if (firstMiddlePage !== undefined && firstMiddlePage > 2) {
    pages.push('ellipsis-start');
  }

  pages.push(...middlePages);

  if (lastMiddlePage !== undefined && lastMiddlePage < pageCount - 1) {
    pages.push('ellipsis-end');
  }

  pages.push(pageCount);

  return pages;
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage = 1,
  itemsPerPage,
  totalCount,
  estimatedTotalCount = 0,
  showNumberOfPages = 5,
  onClick = () => {},
}) => {
  const localTotalCount = Math.max(totalCount || 0, estimatedTotalCount);

  const pageCount = Math.ceil(localTotalCount / itemsPerPage);
  const pageNumbers = generatePageNumbers(currentPage, pageCount, showNumberOfPages);
  const mobilePageNumbers = generateAnchoredPageNumbers(currentPage, pageCount);
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === pageCount || pageCount === 0;
  const startItemNumber = (currentPage - 1) * itemsPerPage + 1;
  const endItemNumber = Math.min(startItemNumber - 1 + itemsPerPage, localTotalCount);

  const handleOnClick = (page: number) => {
    if (page < 1 || page > pageCount) return;

    onClick(page);
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 sm:px-6">
      <div className="flex flex-1 flex-wrap items-center justify-center gap-2 sm:hidden">
        <button
          type="button"
          onClick={() => handleOnClick(currentPage - 1)}
          className={`relative inline-flex h-10 min-w-10 items-center justify-center rounded-md border ${
            isFirstPage
              ? 'border-gray-300 bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          } text-sm font-medium`}
        >
          <span className="sr-only">Previous</span>
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        {pageCount > 0 && (
          <nav
            className="isolate inline-flex max-w-full -space-x-px overflow-hidden rounded-md shadow-sm"
            aria-label="Mobile pagination"
          >
            {mobilePageNumbers.map((page, index) => {
              const isFirstMobilePage = index === 0;
              const isLastMobilePage = index === mobilePageNumbers.length - 1;
              const roundedClass = isFirstMobilePage
                ? 'rounded-l-md'
                : isLastMobilePage
                  ? 'rounded-r-md'
                  : '';

              return typeof page === 'number' ? (
                <button
                  key={page}
                  type="button"
                  onClick={() => handleOnClick(page)}
                  aria-current={page === currentPage ? 'page' : undefined}
                  className={`${
                    page === currentPage
                      ? 'z-10 bg-blue-600 text-white font-semibold'
                      : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                  } ${roundedClass} relative inline-flex h-10 min-w-8 max-w-16 items-center justify-center px-2 text-sm focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
                >
                  <span className="truncate">{page}</span>
                </button>
              ) : (
                <span
                  key={page}
                  className={`${roundedClass} relative inline-flex h-10 min-w-8 items-center justify-center bg-white px-2 text-sm text-gray-500 ring-1 ring-inset ring-gray-300`}
                >
                  ...
                </span>
              );
            })}
          </nav>
        )}
        <button
          type="button"
          onClick={() => handleOnClick(currentPage + 1)}
          className={`relative inline-flex h-10 min-w-10 items-center justify-center rounded-md border ${
            isLastPage
              ? 'border-gray-300 bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          } text-sm font-medium`}
        >
          <span className="sr-only">Next</span>
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          {localTotalCount > 0 ? (
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{startItemNumber}</span> to{' '}
              <span className="font-medium">{endItemNumber}</span>
              {totalCount || isLastPage ? (
                <>
                  {' '}
                  of{' '}
                  <span className="font-medium">
                    {isLastPage ? localTotalCount : totalCount}
                  </span>{' '}
                  results
                </>
              ) : (
                ''
              )}
            </p>
          ) : (
            ''
          )}
        </div>
        <div>
          {pageCount > 0 ? (
            <nav
              className="isolate inline-flex -space-x-px rounded-md shadow-sm"
              aria-label="Pagination"
            >
              <button
                type="button"
                onClick={() => handleOnClick(currentPage - 1)}
                className={`relative inline-flex items-center justify-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 h-11 min-w-11 focus:z-20 focus:outline-offset-0 ${
                  isFirstPage ? 'cursor-not-allowed bg-gray-200 text-gray-500' : 'hover:bg-gray-100'
                }`}
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => handleOnClick(page)}
                  aria-current={page === currentPage ? 'page' : undefined}
                  className={`${
                    page === currentPage
                      ? 'bg-blue-600 text-white font-semibold'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-100'
                  } text-sm text-center focus:z-20 h-11 w-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => handleOnClick(currentPage + 1)}
                className={`relative inline-flex items-center justify-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 h-11 min-w-11 focus:z-20 focus:outline-offset-0 ${
                  isLastPage ? 'cursor-not-allowed bg-gray-200 text-gray-500' : 'hover:bg-gray-100'
                }`}
              >
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          ) : (
            ''
          )}
        </div>
      </div>
    </div>
  );
};

export default Pagination;
